import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BlockView, Block } from '../Content/PageBlocks';
import {
  Alert,
  Box,
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';

type ThemeMode = 'dark' | 'light';

const THEME_STORAGE_KEY = 'obesquito-admin-theme';

function loadThemeMode(): ThemeMode {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function ThemeToggle({
  mode,
  onToggle,
}: {
  mode: ThemeMode;
  onToggle: () => void;
}): JSX.Element {
  return (
    <Button size="small" onClick={onToggle}>
      {mode === 'dark' ? '☀️ Light mode' : '🌙 Dark mode'}
    </Button>
  );
}

interface Me {
  username: string;
  role: string;
}

interface ContentEntry {
  key: string;
  label: string;
  page: string;
  value: string;
  updatedBy: string | null;
  updatedAtUtc: string | null;
}

interface UserRow {
  id: string;
  username: string;
  role: string;
}

interface BlockRow extends Block {
  updatedBy: string | null;
  updatedAtUtc: string | null;
}

interface AssetInfo {
  key: string;
  label: string;
  contentType: string | null;
  size: number;
  updatedBy: string | null;
  updatedAtUtc: string | null;
}

// The pages blocks can be added to - must match BlockPages in the backend.
const BLOCK_PAGES = ['Home', 'Mark of the Knights'];

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.detail) message = body.detail;
    } catch {
      // no JSON body - keep the generic message
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function LoginForm({
  onLogin,
  mode,
  onToggleMode,
}: {
  onLogin: (me: Me) => void;
  mode: ThemeMode;
  onToggleMode: () => void;
}): JSX.Element {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const me = await api<Me>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      onLogin(me);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 10 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Obesquito Admin
        </Typography>
        <form onSubmit={submit}>
          <Stack spacing={2}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={busy}>
              Log in
            </Button>
          </Stack>
        </form>
      </Paper>
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <ThemeToggle mode={mode} onToggle={onToggleMode} />
      </Box>
    </Container>
  );
}

function ContentEditor({ notify }: { notify: (msg: string) => void }): JSX.Element {
  const [entries, setEntries] = useState<ContentEntry[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await api<ContentEntry[]>('/api/content/entries');
      setEntries(data);
      setDrafts(Object.fromEntries(data.map((e) => [e.key, e.value])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (entry: ContentEntry): Promise<void> => {
    try {
      const updated = await api<ContentEntry>(`/api/content/${encodeURIComponent(entry.key)}`, {
        method: 'PUT',
        body: JSON.stringify({ value: drafts[entry.key] }),
      });
      setEntries((prev) => prev.map((e) => (e.key === updated.key ? updated : e)));
      notify(`Saved "${updated.label}"`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const pages = Array.from(new Set(entries.map((e) => e.page)));

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Only on-page text is editable here. SEO (page titles, meta descriptions, sitemap) is kept
        out of this tool on purpose and is edited by hand in the code.
      </Alert>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {pages.map((page) => (
        <Paper key={page} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {page}
          </Typography>
          <Stack spacing={2} divider={<Divider />}>
            {entries
              .filter((e) => e.page === page)
              .map((entry) => {
                const dirty = drafts[entry.key] !== entry.value;
                return (
                  <Box key={entry.key}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <TextField
                        label={entry.label}
                        value={drafts[entry.key] ?? ''}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [entry.key]: e.target.value }))
                        }
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={8}
                      />
                      <Button
                        variant="contained"
                        onClick={() => void save(entry)}
                        disabled={!dirty}
                        sx={{ mt: 1, flexShrink: 0 }}
                      >
                        Save
                      </Button>
                    </Stack>
                    {entry.updatedBy && (
                      <Typography variant="caption" color="text.secondary">
                        Last edited by {entry.updatedBy}
                        {entry.updatedAtUtc &&
                          ` on ${new Date(entry.updatedAtUtc).toLocaleString()}`}
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function BlockBuilder({ notify }: { notify: (msg: string) => void }): JSX.Element {
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, { type: string; content: string }>>({});
  const [error, setError] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const perPage = await Promise.all(
        BLOCK_PAGES.map((page) => api<BlockRow[]>(`/api/blocks/?page=${encodeURIComponent(page)}`))
      );
      const all = perPage.flat();
      setBlocks(all);
      setDrafts(
        Object.fromEntries(all.map((b) => [b.id, { type: b.type, content: b.content }]))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sections');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<void>, successMsg?: string): Promise<void> => {
    setError('');
    try {
      await action();
      if (successMsg) notify(successMsg);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  const addBlock = (page: string, type: 'text' | 'html'): void => {
    void run(
      () =>
        api('/api/blocks/', {
          method: 'POST',
          body: JSON.stringify({
            page,
            type,
            content: type === 'html' ? '<h2>New section</h2>\n<p>Write here...</p>' : 'New paragraph...',
          }),
        }),
      'Section added'
    );
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Sections are extra content the site renders after the fixed page text. Text sections are
        shown exactly as typed; HTML sections let you use headings, links and formatting - the
        preview below each editor shows how it will look. HTML is sanitized before the site
        renders it, so scripts are stripped.
      </Alert>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {BLOCK_PAGES.map((page) => {
        const pageBlocks = blocks.filter((b) => b.page === page);
        return (
          <Paper key={page} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {page}
            </Typography>
            <Stack spacing={3} divider={<Divider />}>
              {pageBlocks.length === 0 && (
                <Typography color="text.secondary">No sections yet.</Typography>
              )}
              {pageBlocks.map((block, index) => {
                const draft = drafts[block.id] ?? { type: block.type, content: block.content };
                const dirty = draft.type !== block.type || draft.content !== block.content;
                return (
                  <Box key={block.id}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TextField
                        select
                        size="small"
                        label="Type"
                        value={draft.type}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [block.id]: { ...draft, type: e.target.value },
                          }))
                        }
                        sx={{ width: 110 }}
                      >
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="html">HTML</MenuItem>
                      </TextField>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        size="small"
                        disabled={index === 0}
                        onClick={() =>
                          void run(() =>
                            api(`/api/blocks/${block.id}/move`, {
                              method: 'POST',
                              body: JSON.stringify({ direction: 'up' }),
                            })
                          )
                        }
                      >
                        Up
                      </Button>
                      <Button
                        size="small"
                        disabled={index === pageBlocks.length - 1}
                        onClick={() =>
                          void run(() =>
                            api(`/api/blocks/${block.id}/move`, {
                              method: 'POST',
                              body: JSON.stringify({ direction: 'down' }),
                            })
                          )
                        }
                      >
                        Down
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          if (window.confirm('Delete this section?')) {
                            void run(
                              () => api(`/api/blocks/${block.id}`, { method: 'DELETE' }),
                              'Section deleted'
                            );
                          }
                        }}
                      >
                        Delete
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!dirty}
                        onClick={() =>
                          void run(
                            () =>
                              api(`/api/blocks/${block.id}`, {
                                method: 'PUT',
                                body: JSON.stringify(draft),
                              }),
                            'Section saved'
                          )
                        }
                      >
                        Save
                      </Button>
                    </Stack>
                    <TextField
                      value={draft.content}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [block.id]: { ...draft, content: e.target.value },
                        }))
                      }
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={14}
                      InputProps={{ sx: { fontFamily: draft.type === 'html' ? 'monospace' : undefined } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Preview
                    </Typography>
                    <Box
                      sx={{
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: 'background.default',
                      }}
                    >
                      <BlockView
                        block={{
                          id: block.id,
                          page: block.page,
                          sortOrder: block.sortOrder,
                          type: draft.type as 'text' | 'html',
                          content: draft.content,
                        }}
                      />
                    </Box>
                    {block.updatedBy && (
                      <Typography variant="caption" color="text.secondary">
                        Last edited by {block.updatedBy}
                        {block.updatedAtUtc && ` on ${new Date(block.updatedAtUtc).toLocaleString()}`}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => addBlock(page, 'text')}>
                  + Add text paragraph
                </Button>
                <Button variant="outlined" onClick={() => addBlock(page, 'html')}>
                  + Add HTML section
                </Button>
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

function MediaManager({ notify }: { notify: (msg: string) => void }): JSX.Element {
  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      setAssets(await api<AssetInfo[]>('/api/assets/'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const upload = async (asset: AssetInfo, file: File): Promise<void> => {
    setError('');
    setBusyKey(asset.key);
    try {
      const form = new FormData();
      form.append('file', file);
      // No Content-Type header here: the browser sets the multipart boundary.
      const res = await fetch(`/api/assets/${encodeURIComponent(asset.key)}`, {
        method: 'PUT',
        body: form,
        credentials: 'same-origin',
      });
      if (!res.ok) {
        let message = `Upload failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) message = body.detail;
        } catch {
          // keep generic message
        }
        throw new Error(message);
      }
      notify(`Uploaded new file for "${asset.label}"`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Replace the site&apos;s media files here (GIF, PNG, JPEG or WebP, max 10 MB). The site
        shows its built-in image until a slot has a file.
      </Alert>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {assets.map((asset) => (
        <Paper key={asset.key} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {asset.label}
          </Typography>
          {asset.size > 0 ? (
            <Box sx={{ mb: 2 }}>
              <img
                src={`/api/assets/${encodeURIComponent(asset.key)}?v=${asset.updatedAtUtc ?? ''}`}
                alt={asset.label}
                style={{ maxWidth: '100%', maxHeight: 240 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {asset.contentType}, {(asset.size / 1024).toFixed(0)} KB
                {asset.updatedBy && ` - uploaded by ${asset.updatedBy}`}
                {asset.updatedAtUtc && ` on ${new Date(asset.updatedAtUtc).toLocaleString()}`}
              </Typography>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No file uploaded yet - the site is showing its built-in image.
            </Typography>
          )}
          <Button variant="contained" component="label" disabled={busyKey === asset.key}>
            {busyKey === asset.key ? 'Uploading...' : 'Upload new file'}
            <input
              hidden
              type="file"
              accept="image/gif,image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void upload(asset, file);
              }}
            />
          </Button>
        </Paper>
      ))}
    </Stack>
  );
}

function UserManager({ me, notify }: { me: Me; notify: (msg: string) => void }): JSX.Element {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('User');
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      setUsers(await api<UserRow[]>('/api/users'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<void>, successMsg: string): Promise<void> => {
    setError('');
    try {
      await action();
      notify(successMsg);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Accounts
        </Typography>
        <Stack spacing={2} divider={<Divider />}>
          {users.map((user) => (
            <Stack key={user.id} direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography sx={{ minWidth: 140, fontWeight: 500 }}>{user.username}</Typography>
              <TextField
                select
                size="small"
                label="Role"
                value={user.role}
                disabled={user.username === me.username}
                onChange={(e) =>
                  void run(
                    () =>
                      api(`/api/users/${user.id}/role`, {
                        method: 'PUT',
                        body: JSON.stringify({ role: e.target.value }),
                      }),
                    `Changed ${user.username}'s role to ${e.target.value}`
                  )
                }
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="User">User</MenuItem>
              </TextField>
              <Button size="small" onClick={() => setResetTarget(user)}>
                Reset password
              </Button>
              <Button
                size="small"
                color="error"
                disabled={user.username === me.username}
                onClick={() => {
                  if (window.confirm(`Delete account "${user.username}"?`)) {
                    void run(
                      () => api(`/api/users/${user.id}`, { method: 'DELETE' }),
                      `Deleted ${user.username}`
                    );
                  }
                }}
              >
                Delete
              </Button>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add account
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Username"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
          />
          <TextField
            size="small"
            label="Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            select
            size="small"
            label="Role"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </TextField>
          <Button
            variant="contained"
            disabled={!newUsername || !newPassword}
            onClick={() =>
              void run(
                () =>
                  api('/api/users', {
                    method: 'POST',
                    body: JSON.stringify({
                      username: newUsername,
                      password: newPassword,
                      role: newRole,
                    }),
                  }).then(() => {
                    setNewUsername('');
                    setNewPassword('');
                  }),
                `Created ${newUsername}`
              )
            }
          >
            Create
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Admin can edit text and manage accounts. User can only edit text.
        </Typography>
      </Paper>

      <Dialog open={resetTarget !== null} onClose={() => setResetTarget(null)}>
        <DialogTitle>Reset password for {resetTarget?.username}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!resetPassword}
            onClick={() => {
              const target = resetTarget;
              if (!target) return;
              void run(
                () =>
                  api(`/api/users/${target.id}/reset-password`, {
                    method: 'POST',
                    body: JSON.stringify({ newPassword: resetPassword }),
                  }),
                `Password reset for ${target.username}`
              ).then(() => {
                setResetTarget(null);
                setResetPassword('');
              });
            }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function MyAccount({ notify }: { notify: (msg: string) => void }): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState('');

  return (
    <Paper sx={{ p: 3, maxWidth: 400 }}>
      <Typography variant="h6" gutterBottom>
        Change my password
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Current password"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <TextField
          label="New password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Button
          variant="contained"
          disabled={!current || !next}
          onClick={() => {
            setError('');
            api('/api/auth/change-password', {
              method: 'POST',
              body: JSON.stringify({ currentPassword: current, newPassword: next }),
            })
              .then(() => {
                setCurrent('');
                setNext('');
                notify('Password changed');
              })
              .catch((err) =>
                setError(err instanceof Error ? err.message : 'Password change failed')
              );
          }}
        >
          Change password
        </Button>
      </Stack>
    </Paper>
  );
}

function AdminPage(): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState('');

  // Dark by default; the choice is per-browser and only affects /admin -
  // the public site keeps its own styling.
  const [mode, setMode] = useState<ThemeMode>(loadThemeMode);
  const theme = useMemo(() => createTheme({ palette: { mode } }), [mode]);
  const toggleMode = (): void => {
    setMode((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Storage unavailable (e.g. private mode): the toggle still works
        // for this visit, it just won't be remembered.
      }
      return next;
    });
  };

  useEffect(() => {
    api<Me>('/api/auth/me')
      .then(setMe)
      .catch(() => {
        // Not logged in - the login form will show.
      })
      .finally(() => setChecked(true));
  }, []);

  if (!checked) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
      </ThemeProvider>
    );
  }
  if (!me) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginForm onLogin={setMe} mode={mode} onToggleMode={toggleMode} />
      </ThemeProvider>
    );
  }

  const isAdmin = me.role === 'Admin';

  const tabs: { label: string; element: JSX.Element }[] = [
    { label: 'Site text', element: <ContentEditor notify={setToast} /> },
    { label: 'Page sections', element: <BlockBuilder notify={setToast} /> },
    { label: 'Media', element: <MediaManager notify={setToast} /> },
    ...(isAdmin ? [{ label: 'Accounts', element: <UserManager me={me} notify={setToast} /> }] : []),
    { label: 'My account', element: <MyAccount notify={setToast} /> },
  ];
  const activeTab = Math.min(tab, tabs.length - 1);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h4">Obesquito Admin</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography color="text.secondary">
              {me.username} ({me.role})
            </Typography>
            <ThemeToggle mode={mode} onToggle={toggleMode} />
            <Button
              onClick={() => {
                void api('/api/auth/logout', { method: 'POST' }).finally(() => setMe(null));
              }}
            >
              Log out
            </Button>
          </Stack>
        </Stack>

        <Tabs value={activeTab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 3 }}>
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>

        {tabs[activeTab].element}

        <Snackbar
          open={toast !== ''}
          autoHideDuration={3000}
          onClose={() => setToast('')}
          message={toast}
        />
      </Container>
    </ThemeProvider>
  );
}

export default AdminPage;
